import {DLSelect, utils} from '@wallaby-ui/core';
import {find, map, pick, sortBy} from 'lodash';
import * as React from 'react';
import {injectIntl, WrappedComponentProps, IntlShape} from 'react-intl';

import {I18N as I18NTextBox} from './TextBox';
import stylesAutoLayout from '../AutoLayout.less';
import AutoLayoutRenderer from 'src/js/lib/auto_layout/auto_layout_renderer';
import AutoLayoutService from 'src/js/lib/auto_layout/auto_layout_service';
import {ContentProperty} from 'src/js/model/auto_layout_content_schema';
import {LayoutProperty} from 'src/js/model/auto_layout_layout_schema';
import {MetadataSchema} from 'src/js/model/auto_layout_metadata_schema';
import {KEYWORD_PROP_CONTENT_VALUE} from 'src/js/model/auto_layout_screen_data_helpers';

interface ValueObjectType {
  value: string;
  label?: string;
  labelCode?: string;
}

interface IdObjectType {
  id: string;
  label?: string;
  labelCode?: string;
}

interface State {
  options: ValueObjectType[];
}

interface Data {
  $value: IdObjectType | null | string;
}

interface NewData {
  $value: IdObjectType | null;
}

interface OldWithNewDataType {
  data?: Data | Record<any, any> | null;
  label?: string;
  newData?: NewData | null;
  pathToData?: string;
}

interface OwnProps {
  content: ContentProperty;
  dataSets: Record<string, any>;
  layout: LayoutProperty;
  metadata: MetadataSchema;
  screenId: string;
  idSuffix?: string;
  intl: IntlShape;
  data?: Data;
  pathToData?: string;
  isReadOnly?: boolean;
  isTextOnly?: boolean;
  inputName?: string;
  label?: string;
  expanded?: boolean;
  onDataChange: (newData: OldWithNewDataType) => void;
  hasError?: boolean;
}

type Props = OwnProps & WrappedComponentProps;

const STATIC_EMPTY_ARRAY: [] = [];
const STATIC_EMPTY_OBJECT: Record<any, any> = {};

class DropDownList extends React.PureComponent<Props, State> {
  static displayName: string = 'DropDownList';

  constructor(props) {
    super(props);

    this.state = {
      options: this.getOptions(),
    };

    this.handleChange = this.handleChange.bind(this);
    this.mapSingleItem = this.mapSingleItem.bind(this);
  }

  handleChange(value: ValueObjectType) {
    const {onDataChange} = this.props;
    onDataChange(this.getOnDataChangeData(value));
  }

  getOnDataChangeData(newData: ValueObjectType): OldWithNewDataType {
    const {
      label = '',
      pathToData = '',
      data = STATIC_EMPTY_OBJECT,
    } = this.props;
    const {options} = this.state;
    let newDataOption: ValueObjectType | undefined | null = null;
    let newDataIdOption: IdObjectType | null = null;
    const ownData = newData ? newData.value : null;
    if (ownData) {
      newDataOption = find(options, (option) => option.value === ownData);
      if (newDataOption) {
        newDataIdOption = {
          id: newDataOption.value,
          label: newDataOption.label,
          labelCode: newDataOption.labelCode,
        };
      }
    }
    return {
      data,
      label,
      newData: {[KEYWORD_PROP_CONTENT_VALUE]: newDataIdOption},
      pathToData,
    };
  }

  getOptions() {
    const {content, dataSets, inputName = '', metadata, layout} = this.props;
    let options;
    // do not load dataset if item is readonly
    if (!layout.getReadOnly()) {
      if (this.isAsync()) {
        const dataSetId = AutoLayoutRenderer.getSelectListId(
          content,
          inputName
        );
        const dataSetOptions = dataSets[dataSetId];
        options = this.getDataSetMappedOptions(dataSetOptions);
      } else {
        const metadataOptions = AutoLayoutRenderer.getSelectOptions(
          metadata.getContent(),
          content,
          inputName
        );
        options = this.getMetadataMappedOptions(metadataOptions);
      }
    }
    return options || STATIC_EMPTY_ARRAY;
  }

  getDataSetMappedOptions(options) {
    const {intl} = this.props;
    return sortBy(
      map(options, (item) => ({
        value: item.name,
        labelCode: item.labelCode,
        label:
          item.labelCode &&
          utils.TranslateUtils.hasTranslation(item.labelCode, intl)
            ? utils.TranslateUtils.translate(item.labelCode, intl)
            : item.label || item.labelCode,
      })),
      ['label']
    );
  }

  getMetadataMappedOptions(options: never[]) {
    return sortBy(
      map(options, (item) => this.mapSingleItem(item)),
      ['label']
    );
  }

  getTextOnlyValue(translatedValue: ValueObjectType | null): string {
    const {data = STATIC_EMPTY_OBJECT, intl} = this.props;
    return data &&
      data[KEYWORD_PROP_CONTENT_VALUE] &&
      data[KEYWORD_PROP_CONTENT_VALUE] !== '' &&
      translatedValue &&
      translatedValue.label
      ? // do not show data with null label #2515
        translatedValue.label
      : utils.TranslateUtils.translate(I18NTextBox.EMPTY_DATA, intl);
  }

  mapSingleItem(item: IdObjectType, suffix?: string): ValueObjectType | null {
    const {intl} = this.props;
    let result: ValueObjectType | null = null;
    if (item) {
      let label: string | undefined;
      if (
        item.labelCode &&
        suffix &&
        utils.TranslateUtils.hasTranslation(item.labelCode + suffix, intl)
      ) {
        label = utils.TranslateUtils.translate(item.labelCode + suffix, intl);
      } else if (
        item.labelCode &&
        utils.TranslateUtils.hasTranslation(item.labelCode, intl)
      ) {
        label = utils.TranslateUtils.translate(item.labelCode, intl);
      } else {
        label = item.label || item.labelCode;
      }

      result = {
        value: item.id,
        label,
        labelCode: item.labelCode,
      };
    }
    return result;
  }

  isAsync(): boolean {
    const {content, inputName = ''} = this.props;
    return AutoLayoutRenderer.isSelectAsync(content, inputName);
  }

  render() {
    const {
      data = {},
      hasError = false,
      idSuffix = '',
      inputName = '',
      isReadOnly = false,
      isTextOnly = false,
      screenId,
    } = this.props;
    const {options} = this.state;
    const selectedValue = this.mapSingleItem(data[KEYWORD_PROP_CONTENT_VALUE]);
    const selectedValueForTitle = this.mapSingleItem(
      data[KEYWORD_PROP_CONTENT_VALUE],
      '.title'
    );
    const textOnlyValue = this.getTextOnlyValue(selectedValue);
    const titleTextOnlyValue = this.getTextOnlyValue(selectedValueForTitle);

    return isTextOnly ? (
      <span
        className={stylesAutoLayout.dlScreenComponentInput}
        title={titleTextOnlyValue}
      >
        {textOnlyValue}
      </span>
    ) : (
      <DLSelect
        className={stylesAutoLayout.dlScreenComponentInput}
        id={AutoLayoutService.getComponentId(screenId, inputName, idSuffix)}
        name={inputName}
        selectedValue={selectedValue}
        isDisabled={isReadOnly}
        onChange={this.handleChange}
        options={options}
        title={selectedValue ? selectedValue.label : null}
        dlHasError={hasError}
      />
    );
  }
}

export default injectIntl(DropDownList);
